/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, FormText, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, isNumber, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getPatientProcedures } from 'app/entities/patient-procedure/patient-procedure.reducer';
import { getEntities as getIntraOPCharts } from 'app/entities/intra-op-chart/intra-op-chart.reducer';
import { getEntities as getPostOPRecoveryRecords } from 'app/entities/post-op-recovery-record/post-op-recovery-record.reducer';
import { BloodProducts } from 'app/shared/model/enumerations/blood-products.model';
import { createEntity, getEntity, reset, updateEntity } from './procedure-blood-products.reducer';

export const ProcedureBloodProductsUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const patientProcedures = useAppSelector(state => state.patientProcedure.entities);
  const intraOPCharts = useAppSelector(state => state.intraOPChart.entities);
  const postOPRecoveryRecords = useAppSelector(state => state.postOPRecoveryRecord.entities);
  const procedureBloodProductsEntity = useAppSelector(state => state.procedureBloodProducts.entity);
  const loading = useAppSelector(state => state.procedureBloodProducts.loading);
  const updating = useAppSelector(state => state.procedureBloodProducts.updating);
  const updateSuccess = useAppSelector(state => state.procedureBloodProducts.updateSuccess);
  const bloodProductsValues = Object.keys(BloodProducts);

  const handleClose = () => {
    navigate(`/procedure-blood-products${location.search}`);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getPatientProcedures({}));
    dispatch(getIntraOPCharts({}));
    dispatch(getPostOPRecoveryRecords({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.timeAdministered !== undefined && typeof values.timeAdministered !== 'number') {
      values.timeAdministered = Number(values.timeAdministered);
    }
    if (values.cursor !== undefined && typeof values.cursor !== 'number') {
      values.cursor = Number(values.cursor);
    }
    if (values.lastupdated !== undefined && typeof values.lastupdated !== 'number') {
      values.lastupdated = Number(values.lastupdated);
    }
    if (values.microSec !== undefined && typeof values.microSec !== 'number') {
      values.microSec = Number(values.microSec);
    }
    if (values.timestampentry !== undefined && typeof values.timestampentry !== 'number') {
      values.timestampentry = Number(values.timestampentry);
    }

    const entity = {
      ...procedureBloodProductsEntity,
      ...values,
      surgery: patientProcedures.find(it => it.id.toString() === values.surgery?.toString()),
      intraOPChart: intraOPCharts.find(it => it.id.toString() === values.intraOPChart?.toString()),
      recoveryRecord: postOPRecoveryRecords.find(it => it.id.toString() === values.recoveryRecord?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {}
      : {
          type: 'WB',
          ...procedureBloodProductsEntity,
          surgery: procedureBloodProductsEntity?.surgery?.id,
          intraOPChart: procedureBloodProductsEntity?.intraOPChart?.id,
          recoveryRecord: procedureBloodProductsEntity?.recoveryRecord?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="tinyMedicRestApp.procedureBloodProducts.home.createOrEditLabel" data-cy="ProcedureBloodProductsCreateUpdateHeading">
            <Translate contentKey="tinyMedicRestApp.procedureBloodProducts.home.createOrEditLabel">
              Create or edit a ProcedureBloodProducts
            </Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="procedure-blood-products-id"
                  label={translate('tinyMedicRestApp.procedureBloodProducts.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('tinyMedicRestApp.procedureBloodProducts.type')}
                id="procedure-blood-products-type"
                name="type"
                data-cy="type"
                type="select"
              >
                {bloodProductsValues.map(bloodProducts => (
                  <option value={bloodProducts} key={bloodProducts}>
                    {translate(`tinyMedicRestApp.BloodProducts.${bloodProducts}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.procedureBloodProducts.timeAdministered')}
                id="procedure-blood-products-timeAdministered"
                name="timeAdministered"
                data-cy="timeAdministered"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.procedureBloodProducts.doseAdministered')}
                id="procedure-blood-products-doseAdministered"
                name="doseAdministered"
                data-cy="doseAdministered"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.procedureBloodProducts.notes')}
                id="procedure-blood-products-notes"
                name="notes"
                data-cy="notes"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.procedureBloodProducts.active')}
                id="procedure-blood-products-active"
                name="active"
                data-cy="active"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.procedureBloodProducts.cursor')}
                id="procedure-blood-products-cursor"
                name="cursor"
                data-cy="cursor"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.procedureBloodProducts.lastupdated')}
                id="procedure-blood-products-lastupdated"
                name="lastupdated"
                data-cy="lastupdated"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.procedureBloodProducts.microSec')}
                id="procedure-blood-products-microSec"
                name="microSec"
                data-cy="microSec"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.procedureBloodProducts.timestampentry')}
                id="procedure-blood-products-timestampentry"
                name="timestampentry"
                data-cy="timestampentry"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                id="procedure-blood-products-surgery"
                name="surgery"
                data-cy="surgery"
                label={translate('tinyMedicRestApp.procedureBloodProducts.surgery')}
                type="select"
                required
              >
                <option value="" key="0" />
                {patientProcedures
                  ? patientProcedures.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <FormText>
                <Translate contentKey="entity.validation.required">This field is required.</Translate>
              </FormText>
              <ValidatedField
                id="procedure-blood-products-intraOPChart"
                name="intraOPChart"
                data-cy="intraOPChart"
                label={translate('tinyMedicRestApp.procedureBloodProducts.intraOPChart')}
                type="select"
                required
              >
                <option value="" key="0" />
                {intraOPCharts
                  ? intraOPCharts.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <FormText>
                <Translate contentKey="entity.validation.required">This field is required.</Translate>
              </FormText>
              <ValidatedField
                id="procedure-blood-products-recoveryRecord"
                name="recoveryRecord"
                data-cy="recoveryRecord"
                label={translate('tinyMedicRestApp.procedureBloodProducts.recoveryRecord')}
                type="select"
                required
              >
                <option value="" key="0" />
                {postOPRecoveryRecords
                  ? postOPRecoveryRecords.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <FormText>
                <Translate contentKey="entity.validation.required">This field is required.</Translate>
              </FormText>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/procedure-blood-products" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default ProcedureBloodProductsUpdate;
